import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismBanner, PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { t } from 'ember-intl';
import { and, not, or } from 'ember-truth-helpers';
import { showDistance } from 'frontend-utils';
import {
  getCssVariableValue,
  SECONDARY,
} from 'frontend-utils/utils/css-variable';
import {
  isPlatformDomain,
  PLATFORM_PRODUCTION_DOMAIN,
} from 'frontend-utils/utils/domain-checks';
import { addZoomControls } from 'frontend-utils/utils/google-maps/zoom-control';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import can from 'lumen/helpers/can';
import type { Event } from 'lumen/models';
import { EVENTS_CATEGORIES } from 'lumen/models/event';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  getGoogleMapsStyles,
  GOOGLE_MAPS_SHARED_CONFIG,
} from 'lumen/utils/constants/google-maps-constants';
import { isGoogleMapsAvailable } from 'lumen/utils/google-maps';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getIconForCategory(category: string) {
  switch (category) {
    case EVENTS_CATEGORIES.CONFERENCE:
      return 'events-conference';
    case EVENTS_CATEGORIES.PERFORMING_ARTS:
      return 'events-performing-arts';
    case EVENTS_CATEGORIES.MUSIC:
      return 'events-music';
    case EVENTS_CATEGORIES.SPORT:
      return 'events-sports';
    default:
      return 'calendar-accepted';
  }
}

interface HotelEventsTooltipsTooltipEventSignature {
  Args: {
    acceptEvent?: () => void;
    declineEvent?: () => void;
    event: Event;
    hidePopover?: () => void;
    isLoading?: boolean;
    resetEvent?: (event: MouseEvent) => void;
  };
}

export default class HotelEventsTooltipsTooltipEvent extends Component<HotelEventsTooltipsTooltipEventSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @tracked mapIsAvailable = true;

  cleanupZoomControls = (): void => {};

  onceSet = false;

  /**
   * Computes the distance of the hotel to the event using the distance utils
   * @return {string} distance in km or mi, depending on locale
   */
  get eventDistance(): string | null {
    const { event } = this.args;
    const { hotel, user } = this.hotelAndMarketService;

    if (hotel && this.hasEventCoordinates && event.areExactCoordinates) {
      return showDistance(
        {
          latitude: event.lat,
          longitude: event.lon,
        },
        hotel.hotelinfo,
        user.locale,
      );
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get eventInformation() {
    // category, startDate, endDate, name, location, visitors (can be null), url
    let endDate = null;
    let location = null;
    const iconName: IconName =
      this.args.event.icon || getIconForCategory(this.args.event.category);
    if (this.args.event.endDate) {
      if (!this.args.event.endDate.isSame(this.args.event.startDate, 'day')) {
        endDate = this.args.event.endDate.format(
          this.dates.partialDateFormat.WEEKDAY_AND_DATE_SHORT,
        );
      }
    }

    if (this.args.event.location) {
      if (this.args.event.location.indexOf(',') !== -1) {
        location = this.args.event.location.split(',');
      } else {
        location = [this.args.event.location];
      }
    }

    let labelString;
    if (this.args.event.labels?.length) {
      // Capitalize each label and separate them with a comma
      labelString = this.args.event.labels
        .map((label) => label.charAt(0).toUpperCase() + label.slice(1))
        .join(', ');
    }

    return {
      name: this.args.event.displayName ?? this.args.event.name,
      startDate: this.args.event.startDate
        ? this.args.event.startDate.format(
            this.dates.partialDateFormat.WEEKDAY_AND_DATE_SHORT,
          )
        : null,
      endDate,
      isCancelled: this.args.event.isCancelled,
      iconName,
      location,
      type: this.args.event.category || null,
      visitors: this.args.event.visitorsFormatted,
      description: labelString || this.args.event.description || null,
      url:
        this.args.event.url ||
        `http://www.google.com/search?q=${encodeURIComponent(
          `${this.args.event.name} ${this.args.event.location}`,
        )}`,
    };
  }

  get hasEventCoordinates(): boolean {
    const { event } = this.args;

    return Boolean(event.lat) && Boolean(event.lon);
  }

  willDestroy(): void {
    super.willDestroy();

    this.cleanupZoomControls();
  }

  addMap(element: HTMLElement): void {
    try {
      // Documentation URL: https://developers.google.com/maps/documentation/javascript/adding-a-google-map#jsFiddle
      // code here will execute within a RunLoop in about 500ms with this == myContext
      const eventCoordinates: google.maps.LatLngLiteral = {
        lat: this.args.event.lat,
        lng: this.args.event.lon,
      };

      const googleMapsContainer = element.querySelectorAll(
        '.google-maps-container',
      )[0] as HTMLElement;

      if (!googleMapsContainer) {
        return;
      }

      const googleMapsConfig = {
        ...GOOGLE_MAPS_SHARED_CONFIG,
      };

      const map = new google.maps.Map(googleMapsContainer, {
        ...googleMapsConfig,
        zoom: this.args.event.areExactCoordinates ? 12 : 8,
        // Set a max zoom if the coordinates aren't exact. Else allow user to zoom in further
        maxZoom: this.args.event.areExactCoordinates ? null : 8,
        center: eventCoordinates,
        // disable mouse dragging
        draggable: false,
        styles: getGoogleMapsStyles().BLACK_AND_WHITE,
      });

      this.cleanupZoomControls = addZoomControls(map);

      // Add hotel marker
      const hotelCoordinates: google.maps.LatLngLiteral = {
        lat: this.hotelAndMarketService.hotel.hotelinfo.latitude,
        lng: this.hotelAndMarketService.hotel.hotelinfo.longitude,
      };

      const markerIconHotel = {
        path: google.maps.SymbolPath.CIRCLE,
        fillOpacity: 1,
        scale: 6,
        strokeWeight: 4,
        fillColor: getCssVariableValue(
          '--prism-color-chart-accent-secondary-figure-emphasis',
        ),
        strokeColor: getCssVariableValue(
          '--prism-color-chart-accent-secondary-figure-emphasis',
        ),
      };

      new google.maps.Marker({
        position: hotelCoordinates,
        map,
        icon: markerIconHotel,
      });

      if (!this.args.event.areExactCoordinates) {
        const circleDrawHelper = (
          googleMap: google.maps.Map,
          eventCoordinatesArgument: { lat: number; lng: number },
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        ) => {
          const radiusValue = 10000;

          // https://developers.google.com/maps/documentation/javascript/shapes
          /* let circle = */

          new google.maps.Circle({
            center: eventCoordinatesArgument,
            clickable: true,
            draggable: false,
            editable: false,
            fillColor: getCssVariableValue(SECONDARY),
            fillOpacity: 0.27,
            map: googleMap,
            radius: radiusValue,
            strokeColor: getCssVariableValue(SECONDARY),
            strokeOpacity: 0.62,
            strokeWeight: 1,
          });
        };

        circleDrawHelper(map, eventCoordinates);
      } else {
        const markerIcon = {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: getCssVariableValue(
            '--prism-color-chart-accent-primary-figure-default',
          ),
          fillOpacity: 1,
          scale: 8,
          strokeWeight: 2,
          strokeColor: getCssVariableValue(
            '--prism-color-border-inverse-emphasis',
          ),
        };

        new google.maps.Marker({
          position: eventCoordinates,
          icon: markerIcon,
          map,
        });
      }
    } catch {
      this.errorTracking.captureMessage('Google maps failure: events tooltip');
    }
  }

  checkAddMap(element: HTMLElement): void {
    if (
      this.args.event.lat &&
      this.args.event.lon &&
      this.args.event.isPopoverActive &&
      !this.onceSet
    ) {
      this.onceSet = true;
      this.addMap(element);
    }
  }

  @action
  didInsertEventTooltip(element: HTMLElement): void {
    if (isGoogleMapsAvailable()) {
      this.checkAddMap(element);
    } else {
      this.mapIsAvailable = false;
    }
  }

  @action
  openInUserManagement(): void {
    const { id, isPrivate } = this.args.event;

    const type = isPrivate ? 'privateeventedition' : 'publiceventedition';
    let redirectId = '';

    if (id && id.indexOf('-') !== -1) {
      redirectId = id.slice(id.indexOf('-') + 1, id.length);

      if (redirectId) {
        // Admin only feature, so no further domain checks necessary here
        const url = isPlatformDomain()
          ? `https://${window.location.hostname}/admin/events/${type}/${redirectId}/change/`
          : `https://${PLATFORM_PRODUCTION_DOMAIN}/admin/events/${type}/${redirectId}/change/`;

        openUrl(url);
      }
    }
  }

  <template>
    <div {{didInsert this.didInsertEventTooltip}}>
      <div
        class="title-container flex items-center justify-between gap-200 pr-500 pl-500"
      >
        {{#if this.access.isStaffUser}}
          <h4
            class="event-title narrow link-out"
            role="button"
            {{on "click" this.openInUserManagement}}
          >
            {{this.eventInformation.name}}
          </h4>
        {{else}}
          <div class="event-title">
            {{this.eventInformation.name}}
          </div>
        {{/if}}
        {{#if @hidePopover}}
          <PrismButton
            @buttonType="ghost"
            @icon="cross"
            @isSmall={{true}}
            @onClick={{@hidePopover}}
          />
        {{/if}}
      </div>
      <div class="m-500 mb-400 flex flex-col gap-400">
        {{#if @event.isUnconfirmed}}
          <PrismBanner
            @bannerMessage={{t "events.tooltip.unconfirmed_warning"}}
            @bannerStyle="warning"
            @icon="warning"
            data-test-id="unconfirmed-event-warning"
          />
        {{/if}}
        <div class="flex items-center gap-200">
          <div>
            <PrismIcon @name="calendar-default" @size="small" />
          </div>
          <div class="text-300--regular">
            {{this.eventInformation.startDate}}
            {{#if this.eventInformation.endDate}}
              -
            {{/if}}
            {{this.eventInformation.endDate}}
          </div>
        </div>
        {{#if this.eventInformation.visitors}}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon @name="user-group" @size="small" />
            </div>
            <div class="text-300--regular">
              {{this.eventInformation.visitors}}
            </div>
          </div>
        {{/if}}
        {{#if this.eventInformation.type}}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon
                @name={{this.eventInformation.iconName}}
                @size="small"
              />
            </div>
            <div class="text-300--regular">
              {{t (concat "events.category." this.eventInformation.type)}}
            </div>
          </div>
        {{/if}}
        {{#if this.eventInformation.description}}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon @name="circle-information-outline" @size="small" />
            </div>
            <div class="text-300--regular">
              {{this.eventInformation.description}}
            </div>
          </div>
        {{/if}}
        {{#if this.eventInformation.url}}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon @name="link" @size="small" />
            </div>
            <div class="text-300--regular">
              <a
                href={{this.eventInformation.url}}
                rel="noopener noreferrer"
                target="_blank"
              >
                {{t "events.url"}}
              </a>
            </div>
          </div>
        {{/if}}
        {{#if this.eventInformation.location}}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon @name="location-pin" @size="small" />
            </div>
            <div>
              <p class="text-300--regular">
                {{#each this.eventInformation.location as |locationLine|}}
                  {{locationLine}}
                {{/each}}
                {{#if
                  (and this.hasEventCoordinates @event.areExactCoordinates)
                }}
                  ({{this.eventDistance}})
                {{/if}}
              </p>
            </div>
          </div>
        {{/if}}
        {{#if
          (and
            (not this.eventInformation.location)
            (and this.hasEventCoordinates @event.areExactCoordinates)
          )
        }}
          <div class="flex items-center gap-200">
            <div>
              <PrismIcon @name="location-pin" @size="small" />
            </div>
            <div>
              <p class="text-300--regular">
                {{t "events.tooltip.distance_to_hotel"}}
                {{#if
                  (and this.hasEventCoordinates @event.areExactCoordinates)
                }}
                  {{this.eventDistance}}
                {{/if}}
              </p>
            </div>
          </div>
        {{/if}}
      </div>
      {{#if (and this.hasEventCoordinates this.mapIsAvailable)}}
        <div class="google-maps-container mb-500"></div>
      {{/if}}
      {{!Popover indicates if this is used in de calendar or not. The tooltip version should not have the accept/decline functionality }}
      {{#if (and (can "events_holidays.author") @hidePopover)}}
        <div class="event-btn-wrapper flex justify-between px-400 py-300">
          <div>
            {{#if
              (and
                this.access.isStaffUser (or @event.isDeclined @event.isAccepted)
              )
            }}
              <PrismButton
                @buttonType="ghost"
                @iconBefore="undo"
                @label={{t "general.reset"}}
                @onClick={{@resetEvent}}
              />
            {{/if}}
          </div>

          <div class="flex gap-200">
            <PrismButton
              @buttonType="secondary"
              @isDisabled={{or @isLoading @event.isDeclined}}
              @label={{t "events.tooltip.decline_event"}}
              @onClick={{@declineEvent}}
            />
            <PrismButton
              @isDisabled={{or @isLoading @event.isAccepted}}
              @label={{t "events.tooltip.accept_event"}}
              @onClick={{@acceptEvent}}
            />
          </div>
        </div>
      {{/if}}

    </div>
  </template>
}
